import React from 'react';
import { Link } from 'react-router-dom';
import {
  List,
  Show,
  useListContext,
  useShowContext,
  useRefresh,
  useDataProvider,
  useGetList,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
} from '@mui/material';
import { AlertTriangle, DollarSign, Unlock, User } from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { AvatarName, StatusBadge } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { CustomDataProvider } from '../types/dataProvider';

// Amounts are stored in pesos (COP, whole numbers).
export const cop = (v?: number | null) =>
  v === undefined || v === null ? '—' : `$${Number(v).toLocaleString('es-CO')}`;
const fmtDate = (v?: string | null) =>
  v
    ? new Date(v).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

interface PaymentItem {
  id: number;
  status: string;
  amount: number;
  currency: string;
  provider: string;
  payment_method: string;
  platform_fee?: number;
  net_amount?: number;
  provider_payment_id?: string;
  provider_reference?: string;
  payout_id?: number;
  // Set by the automatic release sweep when Wompi refused to void a wedged
  // PROCESSING payment, i.e. the money may be real and a human must decide.
  is_flagged?: boolean;
  flagged_reason?: string;
  created_at: string;
  paid_at?: string;
  user_id: number;
  user_name?: string;
  user_email?: string;
  booking_id?: number;
  session_id?: number;
  session_title?: string;
  trainer_id?: number;
  trainer_name?: string;
}
interface RefundItem {
  id: number;
  amount: number;
  reason: string;
  status: string;
  created_at: string;
}
interface WebhookItem {
  id: number;
  event_type: string;
  processed: boolean;
  created_at: string;
}
interface PaymentDetail extends PaymentItem {
  provider_response?: any;
  refunds: RefundItem[];
  webhook_events: WebhookItem[];
}

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  // Processing = money is at the bank and the booking is blocked. These are the
  // only payments an admin ever needs to release, so without this tab the
  // release button is unreachable except by walking in from a booking.
  { value: 'processing', label: 'Processing' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'refunded', label: 'Refunded' },
];
const SORT = [
  { field: 'created_at', label: 'Created' },
  { field: 'amount', label: 'Amount' },
  { field: 'status', label: 'Status' },
  { field: 'id', label: 'ID' },
];
const SORT_LABELS = Object.fromEntries(SORT.map((f) => [f.field, f.label]));

const Tabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.status as string) || 'all';
  return (
    <SegmentedTabs
      tabs={TABS}
      value={current}
      onChange={(v) =>
        setFilters({ ...filterValues, status: v === 'all' ? undefined : v }, displayedFilters)
      }
    />
  );
};

const Stats = () => {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } } as const;
  const approved = useGetList('payments', { ...base, filter: { status: 'approved' } });
  const pending = useGetList('payments', { ...base, filter: { status: 'pending' } });
  const refunded = useGetList('payments', { ...base, filter: { status: 'refunded' } });
  const all = useGetList('payments', { ...base, filter: {} });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Approved" value={n(approved.total)} />
      <StatCard label="Pending" value={n(pending.total)} />
      <StatCard label="Refunded" value={n(refunded.total)} />
      <StatCard label="Total payments" value={n(all.total)} />
    </StatRow>
  );
};

const ListBody = () => {
  const columns: DataColumn<PaymentItem>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    { key: 'user', header: 'Payer', cell: (r) => (r.user_name ? <AvatarName name={r.user_name} subtext={r.user_email} /> : <span className="text-ink-3">—</span>) },
    { key: 'session', header: 'Session', cell: (r) => <span className="truncate text-ink">{r.session_title || '—'}</span> },
    { key: 'trainer', header: 'Trainer', cell: (r) => <span className="text-ink">{r.trainer_name || '—'}</span> },
    { key: 'amount', header: 'Amount', sortable: true, align: 'right', cell: (r) => <span className="tabular-nums font-medium">{cop(r.amount)}</span> },
    { key: 'method', header: 'Method', cell: (r) => <span className="text-[12px] uppercase text-ink-2">{r.payment_method}</span> },
    { key: 'created_at', header: 'Created', sortable: true, cell: (r) => fmtDate(r.created_at) },
    { key: 'status', header: 'Status', sortable: true, cell: (r) => <StatusBadge status={r.status} /> },
  ];
  return (
    <ListScreen
      stats={<Stats />}
      tabs={<Tabs />}
      actions={<SortMenu fields={SORT} />}
      tokens={<FilterTokens fields={[]} sortLabels={SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No payments found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const PaymentList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'created_at', order: 'DESC' }} perPage={25} component="div">
    <SearchIntegration filterKey="q" />
    <ListBody />
  </List>
);

/* ---- Refund dialog ---- */
const RefundDialog = ({ payment, onClose, onSuccess }: { payment: PaymentDetail; onClose: () => void; onSuccess: () => void }) => {
  const [amount, setAmount] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const dataProvider = useDataProvider<CustomDataProvider>();
  const submit = async () => {
    setSubmitting(true);
    try {
      await dataProvider.refundPayment({ id: payment.id, amount: amount ? Number(amount) : null, reason: reason || 'Admin refund' });
      toastSuccess('Refund created');
      onSuccess();
      onClose();
    } catch (e: any) {
      toastError(e?.message || 'Refund failed');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Refund payment #{payment.id}</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-4">
          <p className="text-sm text-ink-2">Leave amount blank for a full refund of {cop(payment.amount)}.</p>
          <MuiTextField fullWidth label="Amount (COP, optional)" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))} />
          <MuiTextField fullWidth multiline rows={3} label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outline" disabled={submitting}>Cancel</Button>
        <Button onClick={submit} className="bg-black text-white hover:bg-ink" disabled={submitting}>{submitting ? 'Refunding…' : 'Refund'}</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---- Release (unblock a booking held by a wedged payment) ---- */
/**
 * A `processing` payment holds its booking: Wompi has a transaction pending at
 * the bank, so we can neither confirm it nor let the user pay again. The
 * automatic sweep tries to void it at Wompi after a per-method TTL; when Wompi
 * refuses — which means the money may be real — the payment is flagged and left
 * for a human. These buttons are that human's decision.
 *
 * "Release" is the safe path: the endpoint re-checks Wompi and voids there
 * before freeing the booking, and refuses outright if the payment turns out
 * approved. "Force" skips provider confirmation entirely, so it is offered only
 * once the sweep has flagged the payment — by which point the safe path has
 * already been tried and refused.
 */
const ReleaseActions = ({ payment, onSuccess }: { payment: PaymentDetail; onSuccess: () => void }) => {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [confirming, setConfirming] = React.useState<null | 'release' | 'force'>(null);
  const [busy, setBusy] = React.useState(false);

  const release = async (force: boolean, notes?: string) => {
    const admin_notes = (notes || '').trim();
    // The endpoint requires at least 3 characters; check here so a too-short
    // note fails as a readable message rather than a 422 validation blob.
    if (admin_notes.length < 3) {
      toastError('Add a note explaining why — at least 3 characters.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await dataProvider.releasePayment({ id: payment.id, force, admin_notes });
      // Report what the endpoint actually did, not which button was pressed:
      // force still attempts a real void first and only falls back, and the
      // payment may have resolved on its own since the page loaded.
      toastSuccess(
        data?.action === 'force_released'
          ? 'Force-released without provider confirmation'
          : data?.action === 'already_resolved'
            ? `Already resolved as ${data.status} — the booking is free`
            : 'Voided at Wompi — the booking is released',
      );
      onSuccess();
    } catch (e: any) {
      toastError(e?.message || 'Release failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setConfirming('release')}
        disabled={busy}
        className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
      >
        <Unlock className="size-4" />
        Release payment
      </button>
      {payment.is_flagged && (
        <button
          onClick={() => setConfirming('force')}
          disabled={busy}
          className="inline-flex h-9 items-center gap-2 rounded-pill border border-error/30 px-4 text-sm font-semibold text-error hover:bg-error-bg disabled:opacity-50"
        >
          <AlertTriangle className="size-4" />
          Force release
        </button>
      )}

      <ConfirmDialog
        open={confirming === 'release'}
        onOpenChange={(o) => setConfirming(o ? 'release' : null)}
        tone="warning"
        title={`Release payment #${payment.id}?`}
        consequence={
          'Re-checks the payment at Wompi and voids it there before freeing the booking, ' +
          'so the user can pay again. If Wompi says the payment was approved the release ' +
          'is refused — refund it instead. Nothing happens unless Wompi confirms the void.'
        }
        confirmLabel="Release payment"
        onConfirm={(reason) => release(false, reason)}
        isLoading={busy}
        reasonRequired
        reasonLabel="Why are you releasing it?"
        reasonPlaceholder="Stored on the payment as the audit trail…"
      />

      <ConfirmDialog
        open={confirming === 'force'}
        onOpenChange={(o) => setConfirming(o ? 'force' : null)}
        tone="destructive"
        title={`Force-release payment #${payment.id}?`}
        consequence={
          `This marks ${cop(payment.amount)} as VOIDED without any confirmation from Wompi ` +
          'and puts the seat back on sale. Wompi already refused to void it, which usually ' +
          'means the money is real. Only continue if you have checked the Wompi dashboard or ' +
          "the bank and confirmed nothing left the customer's account — otherwise they have " +
          'paid for a booking they no longer hold.'
        }
        confirmLabel="Force release"
        onConfirm={(reason) => release(true, reason)}
        isLoading={busy}
        reasonRequired
        reasonLabel="How did you confirm no money moved?"
        reasonPlaceholder="e.g. Wompi dashboard shows the transaction VOIDED; bank confirmed no debit"
      />
    </>
  );
};

/* ---- Detail ---- */
function Card({ title, icon: Icon, children }: { title?: string; icon?: any; children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-hairline bg-background p-5">
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

const ShowContent = () => {
  const { record, isLoading } = useShowContext<PaymentDetail>();
  const refresh = useRefresh();
  const [refundOpen, setRefundOpen] = React.useState(false);
  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading payment…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Payment not found</div>;
  const canRefund = record.status === 'approved';
  // Only a `processing` payment blocks its booking; every other status has
  // either resolved or was never in flight, so there is nothing to release.
  const canRelease = record.status === 'processing';
  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>Payment #{record.id}</h1>
            <StatusBadge status={record.status} />
          </div>
          <p className="mt-1 text-sm text-ink-2">
            {record.user_name}
            {record.session_title && ` · ${record.session_title}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canRefund && (
            <button onClick={() => setRefundOpen(true)} className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2">
              <DollarSign className="size-4" />
              Refund
            </button>
          )}
          {canRelease && <ReleaseActions payment={record} onSuccess={refresh} />}
        </div>
      </div>

      {canRelease && (
        <div className="rounded-card border border-warning/30 bg-warning-bg px-4 py-3 text-sm text-warning">
          <strong>Booking held:</strong> this payment is pending at the bank, so booking
          {record.booking_id ? ` #${record.booking_id}` : ''} cannot be paid again until it resolves.
          {record.flagged_reason
            ? ` The automatic sweep could not clear it: ${record.flagged_reason}`
            : ' The automatic sweep will try to void it at Wompi and release the booking on its own.'}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <Card title="Parties" icon={User}>
            <div className="divide-y divide-hairline">
              <Fact label="Payer" value={record.user_id ? <Link to={`/users/${record.user_id}/show`} className="text-indigo hover:text-indigo-press">{record.user_name || `#${record.user_id}`}</Link> : '—'} />
              {record.user_email && <Fact label="Email" value={record.user_email} />}
              {record.trainer_id && <Fact label="Trainer" value={<Link to={`/trainers/${record.trainer_id}/show`} className="text-indigo hover:text-indigo-press">{record.trainer_name}</Link>} />}
              {record.session_id && <Fact label="Session" value={<Link to={`/sessions/${record.session_id}/show`} className="text-indigo hover:text-indigo-press">{record.session_title || `#${record.session_id}`}</Link>} />}
              {record.booking_id && <Fact label="Booking" value={`#${record.booking_id}`} />}
            </div>
          </Card>

          <Card title={`Refunds (${record.refunds?.length || 0})`}>
            {record.refunds?.length ? (
              <div className="divide-y divide-hairline">
                {record.refunds.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-ink">{cop(r.amount)} · {r.reason}</span>
                    <StatusBadge status={r.status} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ink-2">No refunds.</p>
            )}
          </Card>

          <Card title={`Webhook events (${record.webhook_events?.length || 0})`}>
            {record.webhook_events?.length ? (
              <div className="divide-y divide-hairline">
                {record.webhook_events.map((w) => (
                  <div key={w.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-ink">{w.event_type} · {fmtDate(w.created_at)}</span>
                    <StatusBadge status={w.processed ? 'processed' : 'pending'} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ink-2">No webhook events.</p>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Amounts" icon={DollarSign}>
            <div className="text-2xl font-bold text-ink">{cop(record.amount)}</div>
            <div className="mt-2 divide-y divide-hairline">
              <Fact label="Platform fee" value={cop(record.platform_fee)} />
              <Fact label="Net to trainer" value={cop(record.net_amount)} />
            </div>
          </Card>
          <Card title="Provider">
            <div className="divide-y divide-hairline">
              <Fact label="Provider" value={<span className="uppercase">{record.provider}</span>} />
              <Fact label="Method" value={<span className="uppercase">{record.payment_method}</span>} />
              {record.provider_reference && <Fact label="Reference" value={<span className="text-[12px]">{record.provider_reference}</span>} />}
              {record.provider_payment_id && <Fact label="Txn ID" value={<span className="text-[12px]">{record.provider_payment_id}</span>} />}
              {record.payout_id && <Fact label="Payout" value={`#${record.payout_id}`} />}
            </div>
          </Card>
          <Card title="Timeline">
            <div className="divide-y divide-hairline">
              <Fact label="Created" value={fmtDate(record.created_at)} />
              {record.paid_at && <Fact label="Paid" value={fmtDate(record.paid_at)} />}
            </div>
          </Card>
        </div>
      </div>

      {refundOpen && <RefundDialog payment={record} onClose={() => setRefundOpen(false)} onSuccess={refresh} />}
    </div>
  );
};

export const PaymentShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <ShowContent />
  </Show>
);
